// TestReportContent.js — shared rendering for ViewTest and PrintReport.
//
// Pure rendering — no data fetching, no controls. The wrapper components
// (ViewTest interactive, PrintReport for PDF) supply data via props and
// add their own context-specific controls (Edit button, Download PDF, etc).
//
// Layout structure:
//   <div ref={page1Ref}>
//     [edited banner] [metadata] [interpretation] [disk table]
//     [record history]
//   </div>
//   <div ref={imageRef}>
//     [section heading] [plate image]
//   </div>
//   <div ref={page2Ref}>
//     [page header (case ID + heading)] [audit table]
//   </div>
//
// page2Ref is only attached/rendered when there are 2+ revisions.
// imageRef was split out of page1Ref so PrintReport can place the plate
// image atomically (never sliced mid-image across a page break). When
// `printMode` is true, the image is locked to a 631px × 631px square,
// which produces a 100mm × 100mm image in the final PDF given
// PrintReport's 1200px capture wrapper (631 / 1200 × 539pt = 283pt ≈
// 100mm). ViewTest passes neither ref nor printMode; the divs render
// as one continuous on-screen flow with a responsive full-width image.

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import MultiSourceResultsTable from "./MultiSourceResultsTable";

// Compact metadata row used by both ViewTest and PrintReport for the
// case-metadata / interpretation / record-history sections. Font size
// matches the audit-table cells (MUI default 0.875rem) for a uniform
// "body text" scale across the whole report.
const MetadataRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.6,
      borderBottom: "1px dotted #ccc",
    }}
  >
    <Typography sx={{ fontWeight: "bold", pr: 2, fontSize: "0.875rem" }}>
      {label}:
    </Typography>
    <Typography sx={{ textAlign: "right", fontSize: "0.875rem" }}>
      {value || "N/A"}
    </Typography>
  </Box>
);

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  // Firestore Timestamp has .seconds; allow plain Date objects too.
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  if (timestamp instanceof Date) return timestamp.toLocaleString();
  return "N/A";
};

/**
 * Audit-history table — renders the chronological list of revisions
 * with editor + timestamp + reason. Only shown when there are 2+
 * revisions (callers gate this with revisions.length > 1).
 */
function RevisionAuditTable({ revisions, editorNames }) {
  const { t } = useTranslation();
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell sx={{ fontWeight: "bold", width: "8%" }}>
              {t("printReport.auditColRev")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "22%" }}>
              {t("printReport.auditColEditor")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
              {t("printReport.auditColWhen")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "45%" }}>
              {t("printReport.auditColReason")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {revisions.map((rev) => {
            const editor =
              editorNames[rev.createdByUid] || t("printReport.unknownUser");
            const isInitial = rev.revisionNumber === 1;
            return (
              <TableRow key={rev.id || rev.revisionNumber}>
                <TableCell>{rev.revisionNumber}</TableCell>
                <TableCell>{editor}</TableCell>
                <TableCell>{formatDate(rev.createdAt)}</TableCell>
                <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                  {isInitial ? (
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      {t("printReport.initialSubmission")}
                    </Box>
                  ) : (
                    rev.changeReason || t("printReport.noReasonRecorded")
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Main shared report content. Takes a test object (parent doc shape —
 * top-level fields like caseId, organism, diskAssessments, etc.) plus
 * various lookup data needed for display.
 *
 * Props:
 *   test                    — parent test doc as returned by getTestResultById
 *   submitterName           — display name of the original submitter
 *   editorName              — display name of the latest editor (if isEdited)
 *   organizationName        — org name for the footer
 *   primaryGuideline        — "EUCAST" or "CLSI", drives column highlight
 *   specimenTypeMap         — { code: localizedName } for specimen-type lookup
 *   clinicalIndicationMap   — { englishDisplay: localized } for clinical
 *                             indication lookup. Optional; defaults to empty
 *                             so callers that don't supply it (e.g. legacy
 *                             PrintReport) fall through to the English value.
 *   speciesMap              — { englishDisplay: localized } for species/host
 *                             lookup. Same optional/fallback semantics as
 *                             clinicalIndicationMap.
 *   revisions               — full revisions list (for audit table)
 *   editorNames             — { uid: displayName } map for audit table
 *   page1Ref / page2Ref     — optional refs for PDF capture; both pages
 *                             render in the same DOM flow regardless
 */
export default function TestReportContent({
  test,
  submitterName,
  editorName,
  organizationName,
  primaryGuideline,
  specimenTypeMap,
  clinicalIndicationMap = {},
  speciesMap = {},
  revisions = [],
  editorNames = {},
  page1Ref = null,
  imageRef = null,
  page2Ref = null,
  // When true (PDF capture mode) the plate image is locked to a fixed
  // square so it lands at a deterministic 100mm × 100mm in the final
  // PDF. When false (ViewTest interactive mode) it renders responsive
  // full-width as before.
  printMode = false,
}) {
  const { t } = useTranslation();
  if (!test) return null;

  const imageUrl = test.correctedImageUrl || test.finalImageUrl;
  const showAuditTable = revisions.length > 1;
  const revisionLabel =
    test.currentRevision && test.revisionCount
      ? t("printReport.revisionLabel", {
          current: test.currentRevision,
          total: test.revisionCount,
        })
      : null;

  return (
    <>
      {/* PAGE 1 — main report content */}
      <div ref={page1Ref}>
        {/* Page heading + revision badge */}
        <Typography variant="h3" gutterBottom>
          {t("printReport.title")}
          {revisionLabel && (
            <Box
              component="span"
              sx={{
                ml: 2,
                fontSize: "1.3rem",
                color: "text.secondary",
                fontWeight: "normal",
              }}
            >
              · {revisionLabel}
            </Box>
          )}
        </Typography>

        {/* Edited banner */}
        {test.isEdited && (
          <Alert severity="warning" variant="filled" sx={{ mb: 2 }}>
            {t("printReport.correctedReport")}
          </Alert>
        )}

        {/* Case Metadata */}
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          {t("editTest.caseMetadata")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MetadataRow label={t("findTest.caseId")} value={test.caseId} />
        <MetadataRow
          label={t("editTest.specimenType")}
          value={
            test.specimenType
              ? specimenTypeMap[test.specimenType] || test.specimenType
              : null
          }
        />
        <MetadataRow
          label={t("printReport.species")}
          value={test.species ? speciesMap[test.species] || test.species : null}
        />
        <MetadataRow label={t("editTest.organism")} value={test.organism} />
        <MetadataRow label={t("editTest.notes")} value={test.notes} />

        {/* Interpretation Info */}
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          {t("printReport.interpretationInfo")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MetadataRow
          label={t("printReport.clinicalIndication")}
          value={
            test.clinicalIndication
              ? clinicalIndicationMap[test.clinicalIndication] ||
                test.clinicalIndication
              : null
          }
        />
        <MetadataRow
          label={t(
            "printReport.primaryGuideline",
            "Organization default interpretation standard",
          )}
          value={primaryGuideline}
        />

        {/* Disk Assessments */}
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          {t("editTest.diskAssessments")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MultiSourceResultsTable
          diskAssessments={test.diskAssessments}
          primaryGuideline={primaryGuideline}
        />

        {/* Algorithmic provenance footnote: when "General / Not specified"
            was selected as the clinical indication, the matcher used the
            most-specific breakpoint available for the (organism, host)
            pair regardless of which body site that breakpoint was
            originally authored for. Disclosed here so a clinician
            reading the report knows that specific-site breakpoints
            (UTI, meningitis, pneumonia, etc.) are NOT being
            distinguished from non-site-specific ones. See
            interpretation_algorithm.md Section 11.1. */}
        {(test.clinicalIndication === "General / Not specified" ||
          test.clinicalIndicationCode == null) && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
            >
              <strong>{t("printReport.noteOnIndicationTitle")}</strong>{" "}
              {t("printReport.noteOnIndicationBody")}
            </Typography>
          </Box>
        )}

        {/* Record History — kept minimal. Submitter info and the
            submitting organization are the only details that don't
            already appear in the revision audit table. (Edit-related
            fields like "Corrected by/at/Reason" are intentionally
            omitted here because the audit table on page 2 captures
            the full chronology of edits.) */}
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          {t("viewTest.recordHistory")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MetadataRow
          label={t("printReport.submittedBy")}
          value={submitterName}
        />
        <MetadataRow
          label={t("printReport.submittedAt")}
          value={formatDate(test.timestamp)}
        />
        <MetadataRow
          label={t("printReport.submitterOrganization")}
          value={organizationName}
        />
      </div>

      {/* Plate image — split out of page1Ref so PrintReport can place it
          atomically (never sliced mid-image across a page break). In
          ViewTest this still renders in the same DOM flow below the
          page1Ref content; the ref is harmless when null. */}
      <div ref={imageRef}>
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          {t("printReport.plateImage")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {imageUrl ? (
          <Box>
            <img
              crossOrigin="anonymous"
              src={imageUrl}
              alt="Test plate"
              style={
                printMode
                  ? {
                      // Keyed to the PrintReport 1200px wrapper width:
                      // image takes 100mm/190mm = 52.6% of the PDF
                      // content area, so 52.6% × 1200 ≈ 631px in the
                      // DOM produces a 100mm × 100mm image block in
                      // the final PDF. If the PrintReport wrapper
                      // width changes, recompute this number.
                      width: "631px",
                      height: "631px",
                      objectFit: "contain",
                      display: "block",
                      marginTop: "16px",
                    }
                  : {
                      width: "100%",
                      maxWidth: "1100px",
                      marginTop: "16px",
                    }
              }
            />
            {test.isEdited && test.correctedImageUrl && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                {t("printReport.imageUpdated")}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography>{t("printReport.noImage")}</Typography>
        )}
      </div>

      {/* Revision audit table — rendered only when there are 2+ revisions.
          Flows after the plate image as a normal section; PrintReport
          places it atomically (no mid-table page split) so it lands
          entirely on whichever page has room for it. */}
      {showAuditTable && (
        <div ref={page2Ref}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom>
              {t("printReport.revisionHistory")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <RevisionAuditTable
              revisions={revisions}
              editorNames={editorNames}
            />
          </Box>
        </div>
      )}
    </>
  );
}
